import { type RefObject, useEffect, useState } from 'react';

import {
  E_DESKTOP_FRAME_CONFIGS,
  E_DEVICE_TYPE,
  E_MOBILE_FRAME_CONFIGS,
  SHARED_DEVICE_SCALE_MULTIPLIER,
} from '@common/configs';

export const useDeviceFrameScale = (
  deviceType: E_DEVICE_TYPE,
  containerRef: RefObject<HTMLElement | null>,
  deviceFrameRef: RefObject<HTMLDivElement | null>
): void => {
  useEffect(() => {
    const container = containerRef.current;
    const frameElement = deviceFrameRef.current;
    if (!container || !frameElement) {
      return;
    }

    const frameConfigs = deviceType === E_DEVICE_TYPE.MOBILE ? E_MOBILE_FRAME_CONFIGS : E_DESKTOP_FRAME_CONFIGS;

    const calculateScale = (entries: ResizeObserverEntry[]) => {
      const { width: containerWidth, height: containerHeight } = entries[0].contentRect;
      const containerAspect = containerWidth / containerHeight;
      const frameAspect = frameConfigs.WIDTH / frameConfigs.HEIGHT;

      /**
       * ADAPTIVE INSET LOGIC
       * For Mobile:
       * - If container is ultra-wide (landscape), we want a larger scale (smaller inset).
       * - If container is ultra-tall (portrait), we want a smaller scale (larger inset).
       */
      let dynamicInset = 0.95; // Default for Desktop

      if (deviceType === E_DEVICE_TYPE.MOBILE) {
        if (containerAspect > 1) {
          // Landscape Container: Make the phone slightly larger to avoid 'toothpick' look
          // Interpolate between 0.8 and 0.9 based on wideness
          dynamicInset = Math.min(0.9, 0.75 + containerAspect * 0.05);
        } else {
          // Portrait Container: Make the phone smaller to avoid 'monolith' look
          // More aggressive inset as it gets taller
          dynamicInset = Math.max(0.65, 0.8 * containerAspect);
        }
      }

      const availableWidth = containerWidth * dynamicInset;
      const availableHeight = containerHeight * dynamicInset;

      const adjustedContainerAspect = availableWidth / availableHeight;

      const newScale =
        frameAspect > adjustedContainerAspect
          ? availableWidth / frameConfigs.WIDTH
          : availableHeight / frameConfigs.HEIGHT;

      const finalScale = Math.max(0, Math.min(newScale, frameConfigs.MAX_SCALE));
      frameElement.style.setProperty('--scale', String(finalScale));
    };

    const observer = new ResizeObserver(calculateScale);
    observer.observe(container);
    return () => {
      return observer.disconnect();
    };
  }, [deviceType, containerRef, deviceFrameRef]);
};

export const useSharedDeviceScale = (containerRef: RefObject<HTMLElement | null>) => {
  const [scales, setScales] = useState({ desktop: 1, mobile: 1 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;

      // Calculate base scale relative to Desktop
      // Desktop takes up the majority of the available space
      const baseScale = Math.min(width / E_DESKTOP_FRAME_CONFIGS.WIDTH, height / E_DESKTOP_FRAME_CONFIGS.HEIGHT, 1);

      const newDesktopScale = baseScale;
      const newMobileScale = baseScale * SHARED_DEVICE_SCALE_MULTIPLIER;

      setScales({ desktop: newDesktopScale, mobile: newMobileScale });
      container.style.setProperty('--scale-desktop', String(newDesktopScale));
      container.style.setProperty('--scale-mobile', String(newMobileScale));

      // Generic scale for shared container usage
      container.style.setProperty('--scale', String(newDesktopScale));
    });

    observer.observe(container);
    return () => {
      return observer.disconnect();
    };
  }, [containerRef]);

  return scales;
};
